import * as yup from "yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SpeakingClassLevel } from "../gql/graphql.ts";
import { Col } from "../UI/Layout/Col.tsx";
import { Row } from "../UI/Layout/Row.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { Select } from "@normasteaching/norma-ui";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { getAllSpeakingClassLevelOptions } from "./getAllSpeakingClassLevelOptions.ts";
import { MultipleSelect } from "../UI/Select/MultipleSelect.tsx";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";

export type TopicFormInputs = {
  name: string;
  level: SpeakingClassLevel;
  pdfUrl: string;
  userGroupIds?: string[];
};

type Props = {
  defaultValues?: TopicFormInputs;
  onSave: (data: TopicFormInputs) => Promise<void>;
  disabledMap?: {
    [key in keyof TopicFormInputs]?: boolean;
  };
};

const schema = yup
  .object({
    name: yup.string().required("Specifica un'nome"),
    level: yup
      .mixed<SpeakingClassLevel>()
      .oneOf(Object.values(SpeakingClassLevel))
      .required("Specifica un livello"),
    pdfUrl: yup
      .string()
      .url("Specifica un URL valido")
      .required("Specifica un URL per il PDF"),
    userGroupIds: yup.array(yup.string().required()),
  })
  .required();

export function TopicForm({ defaultValues, onSave, disabledMap }: Props) {
  const levelOptions = getAllSpeakingClassLevelOptions();
  const { userGroups } = useAllUserGroups();
  const userGroupOptions = (userGroups ?? []).map((ug) => ({
    id: ug.id,
    label: ug.name,
  }));
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    watch,
  } = useForm<TopicFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      level: levelOptions[0].id,
      userGroupIds: [],
      ...(defaultValues ?? {}),
    },
  });
  const selectedUserGroupIds = watch("userGroupIds");

  const { withLoading, loading } = useWithLoading();
  const onSubmit: SubmitHandler<TopicFormInputs> = async (data) => {
    await withLoading(async () => onSave(data));
  };

  return (
    <form>
      <Col>
        <Row>
          <InputWithLabel label="Name">
            <TextInput
              errorMessage={errors.name?.message}
              {...register("name")}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label={"Level"}>
            <Controller
              control={control}
              render={({ field }) => {
                return (
                  <Select<SpeakingClassLevel>
                    disabled={disabledMap?.level}
                    errorMessage={errors.level?.message}
                    onChange={field.onChange}
                    value={field.value}
                    options={levelOptions}
                  />
                );
              }}
              name={"level"}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="PDF url">
            <TextInput
              errorMessage={errors.pdfUrl?.message}
              {...register("pdfUrl")}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="User groups">
            {disabledMap?.userGroupIds ? (
              <TextInput
                readOnly
                value={userGroupOptions
                  .filter((o) => (selectedUserGroupIds ?? []).includes(o.id))
                  .map((o) => o.label)
                  .join(", ")}
              />
            ) : (
              <Controller
                control={control}
                name={"userGroupIds"}
                render={({ field }) => (
                  <MultipleSelect
                    placeholder={"Select one or more groups"}
                    errorMessage={errors.userGroupIds?.message as string}
                    options={userGroupOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </InputWithLabel>
        </Row>
        <Row>
          <div className={"flex w-full justify-end"}>
            <PrimaryButton
              loading={loading}
              onClick={handleSubmit(onSubmit)}
              label={"Save"}
            />
          </div>
        </Row>
      </Col>
    </form>
  );
}
